import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ChevronRight, ChevronDown, Copy, Download, Trash2, Code, Zap, Shield, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';

const JsonBeautifierPage = () => {
  const [inputJson, setInputJson] = useState('');
  const [parsedJson, setParsedJson] = useState(null);
  const [error, setError] = useState('');
  const [collapsedPaths, setCollapsedPaths] = useState(new Set());
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);

  // Check if bookmark banner was previously dismissed
  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('json-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputJson(value);
    
    if (!value.trim()) {
      setParsedJson(null);
      setError('');
      return;
    }

    try {
      const parsed = JSON.parse(value);
      setParsedJson(parsed);
      setError('');
    } catch (err) {
      setError(err.message);
      setParsedJson(null);
    }
  };

  const toggleCollapse = (path) => {
    setCollapsedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const copyToClipboard = () => {
    if (parsedJson) {
      navigator.clipboard.writeText(JSON.stringify(parsedJson, null, 2));
    }
  };

  const downloadJson = () => {
    if (parsedJson) {
      const blob = new Blob([JSON.stringify(parsedJson, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'beautified.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const clearAll = () => {
    setInputJson('');
    setParsedJson(null);
    setError('');
    setCollapsedPaths(new Set());
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('json-bookmark-dismissed', 'true');
  };

  const renderJsonValue = (value, key, path = '') => {
    const currentPath = path ? `${path}.${key}` : key;
    const isCollapsed = collapsedPaths.has(currentPath);

    if (value === null) {
      return <span className="text-purple-600">null</span>;
    }

    if (typeof value === 'boolean') {
      return <span className="text-blue-600">{value.toString()}</span>;
    }

    if (typeof value === 'number') {
      return <span className="text-orange-600">{value}</span>;
    }

    if (typeof value === 'string') {
      return <span className="text-green-600">"{value}"</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-600">[]</span>;
      }

      return (
        <div className="ml-4">
          <button
            onClick={() => toggleCollapse(currentPath)}
            className="inline-flex items-center text-gray-600 hover:text-indigo-600 focus:outline-none"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            <span className="ml-1 text-gray-500">[{value.length}]</span>
          </button>
          {!isCollapsed && (
            <div className="ml-4 border-l-2 border-gray-200 pl-4">
              {value.map((item, index) => (
                <div key={index} className="my-1">
                  <span className="text-gray-500">{index}: </span>
                  {renderJsonValue(item, index, currentPath)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return <span className="text-gray-600">{'{}'}</span>;
      }

      return (
        <div className="ml-4">
          <button
            onClick={() => toggleCollapse(currentPath)}
            className="inline-flex items-center text-gray-600 hover:text-indigo-600 focus:outline-none"
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            <span className="ml-1 text-gray-500">{'{'}{keys.length}{'}'}</span>
          </button>
          {!isCollapsed && (
            <div className="ml-4 border-l-2 border-gray-200 pl-4">
              {keys.map((k) => (
                <div key={k} className="my-1">
                  <span className="text-indigo-700 font-medium">"{k}"</span>
                  <span className="text-gray-500">: </span>
                  {renderJsonValue(value[k], k, currentPath)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  return (
    <>
      <Helmet>
        <title>JSON Beautifier — Format & Pretty Print JSON Online | AllSmallTooLS</title>
        <meta name="description" content="Beautify and format JSON data for easy reading and debugging. Fast, free online JSON formatter and validator." />
        <meta name="keywords" content="json beautifier, json formatter, pretty print json, json viewer, json validator" />
        <link rel="canonical" href="https://allsmalltools.com/json-beautifier" />
      </Helmet>

      <div className="space-y-6">
        {/* Bookmark Banner */}
        {showBookmarkBanner && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3 text-center sm:text-left">
                <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    Bookmark this page for quick access to JSON beautifier anytime
                  </p>
                  <p className="text-sm text-gray-600 hidden sm:block">
                    Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    // Try to trigger bookmark dialog directly
                    const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                    const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                    
                    // Attempt legacy methods first
                    if (window.sidebar && window.sidebar.addPanel) {
                      // Firefox
                      window.sidebar.addPanel(document.title, window.location.href, '');
                    } else if (window.external && ('AddFavorite' in window.external)) {
                      // Internet Explorer
                      window.external.AddFavorite(window.location.href, document.title);
                    } else {
                      // Modern browsers - show friendly message
                      const message = `To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your JSON beautifier for future use.`;
                      alert(message);
                    }
                  }}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  <span>Bookmark Now</span>
                </button>

                {/* Close Button - Prominent and on the right */}
                <button
                  onClick={handleDismissBookmarkBanner}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                  aria-label="Dismiss bookmark banner"
                  title="Don't show again"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
              <Code className="h-8 w-8 text-indigo-600" />
              <span>JSON Beautifier</span>
            </h1>
            <p className="text-gray-600">Format, validate, and explore JSON with an interactive viewer</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Box */}
          <Card>
            <CardHeader><CardTitle>Raw JSON Input</CardTitle></CardHeader>
            <CardContent>
              <textarea
                value={inputJson}
                onChange={handleInputChange}
                placeholder='Enter your JSON here... e.g., {"name": "John", "age": 30}'
                aria-label="Raw JSON input"
                className="w-full h-[520px] p-4 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              />
              {error && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
                  <p className="text-red-600 text-sm font-medium">Invalid JSON</p>
                  <p className="text-red-500 text-sm mt-1">{error}</p>
                </div>
              )}
              {!error && parsedJson !== null && <p className="mt-3 text-sm font-medium text-green-700">Valid JSON — formatted automatically</p>}
            </CardContent>
          </Card>

          {/* Output Box */}
          <Card>
            <CardHeader className="space-y-4">
              <CardTitle>Beautified JSON Output</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button onClick={copyToClipboard} disabled={!parsedJson} variant="outline" size="sm"><Copy className="h-4 w-4 mr-2" />Copy</Button>
                <Button onClick={downloadJson} disabled={!parsedJson} variant="outline" size="sm"><Download className="h-4 w-4 mr-2" />Download</Button>
                <Button onClick={clearAll} disabled={!inputJson} variant="outline" size="sm"><Trash2 className="h-4 w-4 mr-2" />Clear</Button>
              </div>
            </CardHeader>
            <CardContent>
            <div className="w-full h-[520px] p-4 border border-gray-300 rounded-lg bg-gray-50 overflow-auto">
              {parsedJson ? (
                <div className="font-mono text-sm">
                  {Array.isArray(parsedJson) ? (
                    renderJsonValue(parsedJson, 'root', '')
                  ) : typeof parsedJson === 'object' ? (
                    <div>
                      {Object.keys(parsedJson).map((key) => (
                        <div key={key} className="my-1">
                          <span className="text-indigo-700 font-medium">"{key}"</span>
                          <span className="text-gray-500">: </span>
                          {renderJsonValue(parsedJson[key], key, '')}
                        </div>
                      ))}
                    </div>
                  ) : (
                    renderJsonValue(parsedJson, 'root', '')
                  )}
                </div>
              ) : (
                <p className="text-gray-400 text-center mt-8">
                  Your beautified JSON will appear here...
                </p>
              )}
            </div>
            </CardContent>
          </Card>
        </div>

      {/* Why Use Our JSON Beautifier Section */}
      <div className="space-y-6">
        <Card className="mt-12">
          <CardHeader>
            <h2 className="text-2xl font-bold text-gray-900">Why Use Our JSON Beautifier?</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col items-start space-y-3">
                <div className="bg-indigo-100 p-3 rounded-lg">
                  <Code className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Format & Validate</h3>
                <p className="text-gray-600">
                  Beautify JSON with automatic formatting and syntax validation. Our JSON formatter detects errors and makes JSON readable with proper indentation.
                </p>
              </div>
              
              <div className="flex flex-col items-start space-y-3">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Zap className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Interactive Viewer</h3>
                <p className="text-gray-600">
                  Visualize JSON structure with expand/collapse functionality. Navigate complex JSON objects easily. Our JSON beautifier makes large datasets manageable.
                </p>
              </div>
              
              <div className="flex flex-col items-start space-y-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Syntax Highlighting</h3>
                <p className="text-gray-600">
                  Color-coded JSON output for easy reading. Distinguish strings, numbers, booleans, and objects at a glance. Perfect for debugging JSON data.
                </p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">
                Need to format JSON for API development, debugging, or data analysis? Our free JSON beautifier makes JSON readable and validates syntax errors. Perfect for developers working with REST APIs, configuration files, or data structures. Try our JSON formatter tool today!
              </p>
              <div className="mt-4 flex flex-wrap gap-4">
                <Link to="/online-notepad" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → Online Notepad
                </Link>
                <Link to="/text-case-converter" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → Case Converter
                </Link>
                <Link to="/text-find-replace" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → Find and Replace
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How to Use JSON Beautifier Section */}
        <Card className="mt-6">
          <CardHeader>
            <h3 className="text-xl font-bold text-gray-900">How to Beautify JSON Online</h3>
          </CardHeader>
          <CardContent>
            <ol className="space-y-4 text-gray-700">
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-semibold">1</span>
                <div>
                  <strong>Paste JSON Code:</strong> Enter or paste your JSON data in the left input box. The JSON beautifier accepts minified or unformatted JSON.
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-semibold">2</span>
                <div>
                  <strong>Automatic Validation:</strong> The tool validates JSON syntax automatically. If errors exist, they're displayed immediately with error messages.
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-semibold">3</span>
                <div>
                  <strong>View Formatted JSON:</strong> See beautified JSON in the right panel with syntax highlighting. Click arrows to expand/collapse nested objects and arrays.
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-semibold">4</span>
                <div>
                  <strong>Copy or Download:</strong> Copy the formatted JSON to clipboard or download as a .json file. Use the Clear button to reset and format new JSON.
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card className="mt-6">
          <CardHeader>
            <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                  className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900">Is the JSON beautifier free?</h3>
                  {openFaq === 0 ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === 0 && (
                  <div className="p-4 bg-white">
                    <p className="text-gray-700">Yes! Our JSON formatter is completely free. Format and validate unlimited JSON data without registration or fees.</p>
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                  className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900">What does JSON beautifier do?</h3>
                  {openFaq === 1 ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === 1 && (
                  <div className="p-4 bg-white">
                    <p className="text-gray-700">A JSON beautifier formats minified JSON into readable, properly indented code with syntax highlighting. It also validates JSON syntax and helps visualize data structure.</p>
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                  className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900">Does it validate JSON syntax?</h3>
                  {openFaq === 2 ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === 2 && (
                  <div className="p-4 bg-white">
                    <p className="text-gray-700">Yes! Our tool validates JSON automatically. If your JSON has syntax errors, it displays error messages to help you fix the issues before formatting.</p>
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                  className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900">Is my JSON data secure?</h3>
                  {openFaq === 3 ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === 3 && (
                  <div className="p-4 bg-white">
                    <p className="text-gray-700">Absolutely! All JSON formatting happens in your browser. Your data is never uploaded to any server. Complete privacy for all JSON operations.</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default JsonBeautifierPage;
